import React from "react";
import { format } from "d3-format";
import {
  TooltipBodyRow,
  TooltipBodyRowLabel,
  TooltipBodyRowValue,
} from "@atlas-int/viz/shared";
import { ColorDot } from "@atlas-int/ui";
import { ProductClass } from "@atlas-int/data/types";
import { getColorMap } from "@atlas-int/viz";
import { UIView } from "@atlas-int/data";

import type {
  MarketShareDatum,
  ProductMetadataByLevel,
} from "@atlas-int/viz/shared";

interface MarketShareTooltipContentProps {
  datum: MarketShareDatum;
  view: UIView;
  productClass?: ProductClass;
  productMetadata?: ProductMetadataByLevel;
}

/**
 * Tooltip content for Market Share chart
 */
export const MarketShareTooltipContent: React.FC<
  MarketShareTooltipContentProps
> = ({ datum, view, productClass, productMetadata }) => {
  const { dataForYear } = datum;

  const matchingLevelProductMetadata =
    productMetadata && productMetadata.section;

  const formatAsPercentage = (value: number): string => {
    let valueAsPercentage = value * 100;
    if (valueAsPercentage == 0) {
      return format(".0%")(value);
    } else {
      return format(".2%")(value);
    }
  };

  let useColorMap = getColorMap({ view, productClass });

  return (
    <>
      {dataForYear &&
        dataForYear
          .sort((productA: { globalMarketShare: number }, productB: { globalMarketShare: number }) => {
            return productB.globalMarketShare - productA.globalMarketShare;
          })
          .map((productDatumForYear: { productId: string; globalMarketShare: number }) => {
            let sectorProductId = productDatumForYear.productId;
            let globalMarketShare = productDatumForYear.globalMarketShare;
            let matchingProductDatum = matchingLevelProductMetadata
              ? matchingLevelProductMetadata.find(
                  (productDatum) =>
                    productDatum.productId === sectorProductId,
                )
              : undefined;

            let nameShortEn;
            if (matchingProductDatum)
              nameShortEn = matchingProductDatum.nameShortEn;

            let sectorColor;
            if (useColorMap && useColorMap.has(sectorProductId)) {
              sectorColor = useColorMap.get(sectorProductId);
            } else {
              sectorColor = undefined;
            }

            let sectorColorDot = sectorColor ? (
              <ColorDot $color={sectorColor} style={{ marginRight: "0.33rem" }} />
            ) : null;

            return (
              <TooltipBodyRow key={sectorProductId}>
                <TooltipBodyRowLabel>
                  {sectorColorDot}
                  {nameShortEn}
                </TooltipBodyRowLabel>
                <TooltipBodyRowValue>
                  {formatAsPercentage(globalMarketShare)}
                </TooltipBodyRowValue>
              </TooltipBodyRow>
            );
          })}
    </>
  );
};

/**
 * Gets the tooltip header for Market Share chart
 */
export const getMarketShareTooltipHeader = (datum: MarketShareDatum): React.ReactNode => {
  const { year } = datum;
  return `Year: ${year}`;
};

